// src/Pages/EventPersonOversigt/EventPersonOversigt.tsx
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Accordion,
    Container,
    Divider,
    Group,
    Loader,
    Paper,
    Stack,
    Text,
} from "@mantine/core";

import type { Language } from "./types";
import { texts } from "./texts";
import { useEventPersonOverview } from "./hooks/useEventPersonOverview";

import HeaderCard from "./components/HeaderCard";
import DrinksSection from "./components/DrinksSection";
import ShiftsSection from "./components/ShiftsSection";
import TicketsSection from "./components/TicketsSection";
import FilesSection from "./components/FilesSection";
import InfoSection from "./components/InfoSection";
import TransportSection from "./components/TransportSection";
import ExperienceSection from "./components/ExperienceSection";
import VipParkingSection from "./components/VipParkingSection";
import DriverRidesSection from "./components/DriverRidesSection";
import FoodOrdersSection from "./components/FoodOrdersSection";
import TrainingSection from "./components/TrainingSection";

export default function EventPersonOversigt() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const webUrl = process.env.REACT_APP_WEB_URL;

    const navigate = useNavigate();
    const { eventId, personId } = useParams();

    const [language, setLanguage] = useState<Language>("en");
    const t = texts[language];

    const eid = Number(eventId);
    const pid = Number(personId);

    const [experienceLoading, setExperienceLoading] = useState(false);
    const [experienceError, setExperienceError] = useState<string | null>(null);

    const {
        loading,
        eventInfo,
        personInfo,
        fileSections,
        shiftsSorted,
        validTickets,
        activeDrinkBars,
        activeDriverRequests,
        canSeeExperience,
        showTransport,
        hasVipParking,
        personFunctions,
        addDriverLog,
    } = useEventPersonOverview({
        apiUrl,
        eid,
        pid,
    });

    if (!eid || !pid || Number.isNaN(eid) || Number.isNaN(pid)) {
        return (
            <Container size="sm" py="md">
                <Paper withBorder p="md" radius="md">
                    <Text c="red">{t.invalidLink}</Text>
                </Paper>
            </Container>
        );
    }
    const functionNames = personFunctions
        .map((f) => f.functionName)
        .filter((name): name is string => !!name);

    const showTraining = personFunctions.some(
        (f) => [102,103,104,105,106,134,137,108,107].includes(Number(f.functionId)) // SIMON SKAL LAVES Bedre måde
    );

    function goToTransportBooking() {
        navigate(`/transport-route/book?eventId=${eid}`);
    }

    function goToTransportBookingDeparture() {
        navigate(`/transport/departure/${eid}`);
    }

    async function createExperienceAndGo() {
        if (!apiUrl || !eid || !pid) return;

        setExperienceError(null);
        setExperienceLoading(true);

        try {
            const res = await fetch(`${apiUrl}/api/experience/admin/tokens`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    eventId: eid,
                    personId: pid,
                    expiresInDays: 30,
                }),
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || "Kunne ikke oprette experience token.");
            }

            const data = await res.json();
            const token = data.token ?? data.Token ?? data?.tokenDto?.token;

            if (!token) {
                throw new Error("API returnerede ikke et token.");
            }

            navigate(`/experience/book?token=${encodeURIComponent(token)}`);
        } catch (err: any) {
            setExperienceError(err?.message ?? "Fejl ved oprettelse af experience.");
        } finally {
            setExperienceLoading(false);
        }
    }
    return (
        <Container size="sm" py="md">
            <Stack gap="md">
                <HeaderCard
                    eventInfo={eventInfo}
                    personInfo={personInfo}
                    eid={eid}
                    pid={pid}
                    language={language}
                    setLanguage={setLanguage}
                    t={t}
                    functions={functionNames}
                />

                {loading ? (
                    <Group>
                        <Loader color="red" size="sm" />
                        <Text>{t.loading}</Text>
                    </Group>
                ) : (
                    <Accordion multiple variant="separated">
                            <DrinksSection
                                drinkBars={activeDrinkBars}
                                t={t}
                                onSelectBar={(eventBarId, barName) =>
                                    navigate(
                                        `/form-drink-order/${eid}/${pid}` +
                                        `?bar=${eventBarId}` +
                                        `&barName=${encodeURIComponent(barName)}`
                                    )
                                }
                            />
                            <FoodOrdersSection
                                apiUrl={apiUrl}
                                eventId={eid}
                                personId={pid}
                                language={language}
                                t={t}
                            />

                        <ShiftsSection
                            shifts={shiftsSorted}
                            language={language}
                            t={t}
                        />

                        <TicketsSection
                            tickets={validTickets}
                            t={t}
                        />

                        <FilesSection
                            fileSections={fileSections}
                            apiUrl={apiUrl}
                            t={t}
                        />

                        <InfoSection
                            eventInfo={eventInfo}
                            t={t}
                        />

                        <TransportSection
                            show={showTransport}
                            loading={false}
                            error={null}
                            onBookTransport={goToTransportBooking}
                            onCreateDeparture={goToTransportBookingDeparture}
                            t={t}
                            />
                            <TrainingSection
                                show={showTraining}
                                eventId={eid}
                                personId={pid}
                                language={language}
                            />

                        <ExperienceSection
                            show={canSeeExperience}
                            loading={experienceLoading}
                            error={experienceError}
                            onCreateExperience={createExperienceAndGo}
                        />

                        <VipParkingSection
                            show={hasVipParking}
                            webUrl={webUrl}
                            eid={eid}
                            pid={pid}
                        />

                        <DriverRidesSection
                            requests={activeDriverRequests}
                            language={language}
                            onAddDriverLog={addDriverLog}
                            t={t}
                        />
                    </Accordion>
                )}

                <Divider />

                <Text size="xs" c="dimmed">
                    {t.mobileTip}
                </Text>
            </Stack>
        </Container>
    );
}